/**
 * <selection-total-component> - live "total with selections" row for systems with required
 * tailpipes and optional add-ons.
 *
 * Display-only companion to <required-tips-component> and <optional-addons-component>: it sums the
 * product's current variant price plus every selected tip / ticked extra (each input carries its
 * price in cents via data-price) and shows the total above the buy buttons. Hidden until something
 * is selected, so untouched pages look exactly as they do today.
 *
 * It deliberately does NOT rewrite the main price block: the price and the Afterpay message under
 * it stay keyed to the system itself, and the cart remains the source of truth at checkout.
 */

import { ThemeEvents } from '@theme/events';

class SelectionTotalComponent extends HTMLElement {
  /** @type {number} */ #basePrice = 0;
  /** @type {string} */ #currency = 'AUD';
  /** @type {AbortController} */ #abort = new AbortController();
  /** @type {Element | Document} */ #scope = document;

  connectedCallback() {
    this.#basePrice = Number(this.getAttribute('data-base-price')) || 0;
    this.#currency = this.getAttribute('data-currency') || 'AUD';
    this.#scope = this.closest('.shopify-section') || document;

    const { signal } = this.#abort;

    // Any tip/extra selection change in this section recomputes the total.
    this.#scope.addEventListener('change', this.#onChange, { signal });

    // Variant switches move the base price (systems are single-variant today; stay robust anyway).
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (
      !target.classList.contains('required-tips__radio') &&
      !target.classList.contains('optional-addons__checkbox')
    ) {
      return;
    }
    this.#render();
  };

  /** @param {import('./events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const productId = this.getAttribute('data-product-id');
    if (productId && String(event.detail?.data?.productId) !== String(productId)) return;

    const price = Number(event.detail?.resource?.price);
    if (Number.isFinite(price) && price > 0) {
      this.#basePrice = price;
    }
    this.#render();
  };

  /** All currently selected tip/extra inputs in this section. */
  #selections() {
    return /** @type {HTMLInputElement[]} */ ([
      ...this.#scope.querySelectorAll(
        '.required-tips__radio:checked:not(:disabled), .optional-addons__checkbox:checked:not(:disabled)'
      ),
    ]);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: this.#currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #render() {
    const row = this.querySelector('.selection-total__row');
    const amount = this.querySelector('.selection-total__amount');
    if (!(row instanceof HTMLElement) || !amount) return;

    const selections = this.#selections();
    if (selections.length === 0) {
      row.hidden = true;
      return;
    }

    const total = selections.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), this.#basePrice);
    amount.textContent = this.#formatMoney(total);
    row.hidden = false;
  }
}

if (!customElements.get('selection-total-component')) {
  customElements.define('selection-total-component', SelectionTotalComponent);
}
